import { useState, useEffect, useRef } from "react";
import { useIsMutating } from "@tanstack/react-query";
import { ChevronDown, Code2, Trash2, MessageSquare, Brain, PenLine, Crosshair, List, Image, Video, Music, Info, GripVertical } from "lucide-react";
import SettingsPageShell from "../../components/Settings/SettingsPageShell";
import { PrimaryButton, CompactButton, ThirdButton } from "../../components/Button/Buttons";
import { Plus } from "lucide-react";
import { ConfirmDialog } from "../../components/shared/ConfirmDialog";
import AlertBanner from "../../components/shared/AlertBanner/AlertBanner";
import Select from "../../components/Inputs/Select";
import CustomTooltip from "../../components/Tooltip/CustomTooltip";
import { useGetAllModels, useUpdateModel, useUpdateModelOrder, useDeleteModel, useCreateModel, useGetModelDefaults, useUpdateModelDefaults } from "../../api/queryHooks";
import UserAuthStore from "../../store/userStore";
import useFeedback from "../../hooks/useFeedback";
import Loader from "../../components/Loader/Loader";
import QUERYKEYS from "../../api/queryKeys";

// ─── Advanced config field reference (shown via info tooltip) ────────────────
// These fields aren't in the ADD_MODEL_TEMPLATE since they're advanced/optional,
// but they're the ones most likely to be set incorrectly, so documenting them
// inline here avoids the JSON-editor UX limitation of not being able to add
// comments directly in the template JSON.
const ADVANCED_FIELDS_HELP = (
  <div className="space-y-1.5 max-w-[280px]">
    <p><span className="font-semibold">thinking_type</span> — required for Claude when thinking_enabled is true. Use "adaptive" for current models or "enabled" with a positive thinking_budget for legacy models.</p>
    <p><span className="font-semibold">thinking_display</span> — use "summarized" to emit Claude reasoning into Activity, or "omitted" to keep it hidden.</p>
    <p><span className="font-semibold">reasoning_effort</span> — enables reasoning (e.g. "low"/"medium"/"high") for models that support it.</p>
    <p><span className="font-semibold">use_responses_api</span> — routes to OpenAI's /v1/responses instead of /v1/chat/completions. Required whenever reasoning_effort + tools are combined, or OpenAI rejects the request.</p>
  </div>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────

const usageRank = (model) => (model.usage_type === "user" ? 0 : 1);

const sortModels = (models) => [...(models ?? [])].sort((a, b) => {
  const rankDiff = usageRank(a) - usageRank(b);
  if (rankDiff !== 0) return rankDiff;
  return (typeof a.sort_order === "number" ? a.sort_order : 99)
    - (typeof b.sort_order === "number" ? b.sort_order : 99);
});

// ─── Usage type badge ─────────────────────────────────────────────────────────
const USAGE_STYLES = {
  user:   "bg-fill-emerald-100 text-ink-emerald-700 dark:bg-fill-emerald-900/30 dark:text-ink-emerald-400",
  system: "bg-fill-gray-100 text-ink-gray-400 dark:bg-fill-hex-2d2d30 dark:text-ink-gray-500",
};

const UsageBadge = ({ usageType }) => {
  const style = USAGE_STYLES[usageType] ?? "bg-fill-red-100 text-ink-red-700 dark:bg-fill-red-900/30 dark:text-ink-red-400";
  return (
    <span className={`whitespace-nowrap text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${style}`}>
      {usageType ?? "—"}
    </span>
  );
};

// ─── Media type badge ─────────────────────────────────────────────────────
const MEDIA_STYLES = {
  text:  "bg-fill-blue-100 text-ink-blue-700 dark:bg-fill-blue-900/30 dark:text-ink-blue-400",
  image: "bg-fill-hex-ede9fe text-ink-hex-6d28d9 dark:bg-fill-hex-4c1d95/30 dark:text-ink-violet-400",
  audio: "bg-fill-hex-ffedd5 text-ink-hex-c2410c dark:bg-fill-hex-7c2d12/30 dark:text-ink-hex-fb923c",
  video: "bg-fill-hex-fce7f3 text-ink-hex-be185d dark:bg-fill-hex-831843/30 dark:text-ink-hex-f472b6",
};
// Same palette for the initials badge background
const BADGE_STYLES = {
  text:  "bg-fill-blue-100 text-ink-blue-700 dark:bg-fill-blue-900/30 dark:text-ink-blue-400",
  image: "bg-fill-hex-ede9fe text-ink-hex-6d28d9 dark:bg-fill-hex-4c1d95/30 dark:text-ink-violet-400",
  audio: "bg-fill-hex-ffedd5 text-ink-hex-c2410c dark:bg-fill-hex-7c2d12/30 dark:text-ink-hex-fb923c",
  video: "bg-fill-hex-fce7f3 text-ink-hex-be185d dark:bg-fill-hex-831843/30 dark:text-ink-hex-f472b6",
  default: "bg-fill-indigo-50 text-ink-primary dark:bg-fill-hex-1e293b dark:text-ink-accent",
};
const MediaBadge = ({ type }) => {
  const style = MEDIA_STYLES[type] ?? "bg-fill-gray-100 text-ink-gray-400 dark:bg-fill-hex-2d2d30 dark:text-ink-gray-500";
  return (
    <span className={`photon-metadata-label whitespace-nowrap text-[10px] font-semibold px-1.5 py-0.5 rounded-full capitalize ${style}`}>
      {type ?? "—"}
    </span>
  );
};

// ─── Column header row ────────────────────────────────────────────────────────
const COL_HDR = "text-[9px] font-semibold uppercase tracking-wider text-ink-gray-400 dark:text-ink-gray-500";
const MODEL_GRID = "grid grid-cols-[1.25rem_2rem_minmax(0,1.6fr)_4rem_minmax(0,1fr)_4.5rem_5rem_1rem] items-center gap-3 px-1";
const ColumnHeaders = () => (
  <div className={`${MODEL_GRID} pb-1.5 border-b border-edge-gray-200 dark:border-edge-gray-700 mb-0.5`}>
    <div />
    <div />
    <div className={COL_HDR}>Model</div>
    <div className={COL_HDR}>Media</div>
    <div className={COL_HDR}>Company / Provider</div>
    <div className={`text-center ${COL_HDR}`}>Multiplier</div>
    <div className={`text-right ${COL_HDR}`}>Usage Type</div>
    <div />
  </div>
);

// ─── Single model row ─────────────────────────────────────────────────────────
const ADD_MODEL_TEMPLATE = JSON.stringify(
  {
    model_id: "your-model-id",
    model_name: "Model Display Name",
    company: "Google",
    provider: "google",
    media_type: "text",
    region: "us-central1",
    usage_type: "user",
    usage_multiplier: 1,
    description: "Describe this model",
  },
  null,
  2
);

const buildJsonString = (model) => {
  // Ordering is managed by the row controls, not the JSON editor.
  const { model_id, sort_order, updated_by, updated_at, created_by, created_at, ...rest } = model;
  return JSON.stringify(rest, null, 2);
};

const ModelRow = ({ model, orgId, userId, defaults, isLast, orderControls, orderSaving }) => {
  const [open, setOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(false);
  const rowRef = useRef(null);

  const isDefaultModel = defaults && Object.values(defaults).includes(model.model_id);

  const [jsonDraft, setJsonDraft] = useState(() => buildJsonString(model));
  const [savedJson, setSavedJson] = useState(() => buildJsonString(model));
  const [jsonError, setJsonError] = useState(null);

  const { showFeedback } = useFeedback();
  const { mutate: updateModel, isPending } = useUpdateModel();
  const { mutate: deleteModel, isPending: isDeleting } = useDeleteModel();

  const isJsonDirty = jsonDraft !== savedJson;

  const handleJsonChange = (e) => {
    const val = e.target.value;
    setJsonDraft(val);
    try { JSON.parse(val); setJsonError(null); }
    catch (err) { setJsonError(`Invalid JSON: ${err.message}`); }
  };

  const handleSaveJson = () => {
    let parsed;
    try { parsed = JSON.parse(jsonDraft); } catch { return; }
    const updates = Object.fromEntries(
      Object.entries(parsed).map(([k, v]) => [k.toUpperCase(), v])
    );
    updates.SORT_ORDER = model.sort_order ?? 99;
    updateModel(
      { orgId, userId, modelId: model.model_id, updates, replace: true },
      {
        onSuccess: () => { setSavedJson(jsonDraft); showFeedback(`${model.model_name ?? model.model_id} updated`, "success"); },
        onError:   () => showFeedback("Failed to save changes", "error"),
      }
    );
  };

  return (
    <div
      ref={rowRef}
      draggable={!orderSaving && !open}
      onDragStart={(event) => orderControls.onDragStart(event, rowRef.current)}
      onDragEnd={orderControls.onDragEnd}
      className={`group select-none hover:bg-fill-hex-fafafa dark:hover:bg-fill-deep-surface/40 transition-colors rounded-md ${isLast ? "" : "border-b border-edge-gray-100 dark:border-edge-hex-2d2d30"} ${
        orderControls.isDragging
          ? "opacity-70 bg-fill-sidebar-surface dark:bg-fill-navigation-active border border-edge-primary/20 dark:border-edge-hex-3c5567/100"
          : ""
      } ${orderControls.isTarget ? "bg-fill-indigo-50 dark:bg-fill-hex-1e293b" : ""}`}
      onDragOver={orderControls.onDragOver}
      onDrop={orderControls.onDrop}
    >
      {/* Main data row */}
      <div
        onClick={() => setOpen((o) => !o)}
        className={`${MODEL_GRID} w-full pt-2.5 pb-1 cursor-grab active:cursor-grabbing`}
      >
        <button
          type="button"
          draggable={!orderSaving}
          disabled={orderSaving}
          aria-label={`Reorder ${model.model_name ?? model.model_id}`}
          aria-describedby="model-order-help"
          title="Drag to reorder, or use Up and Down arrow keys"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={orderControls.onKeyDown}
          className="w-5 flex-shrink-0 cursor-grab active:cursor-grabbing text-ink-gray-400 hover:text-ink-primary opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity duration-150 disabled:opacity-0 disabled:cursor-wait"
        >
          <GripVertical className="w-4 h-4" />
        </button>
        {/* Initials badge — coloured by media type for quick visual scanning */}
        <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-[10px] font-bold select-none ${BADGE_STYLES[model.media_type] ?? BADGE_STYLES.default}`}>
          {(model.model_name ?? model.model_id).slice(0, 2).toUpperCase()}
        </div>

        {/* Model name + id */}
        <div className="min-w-0">
          <p className="text-[13px] font-medium text-ink-ink dark:text-ink-white truncate">
            {model.model_name ?? model.model_id}
          </p>
          <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500 truncate">{model.model_id}</p>
        </div>

        {/* Media type */}
        <div className="min-w-0">
          <MediaBadge type={model.media_type} />
        </div>

        {/* Provider */}
        <div className="min-w-0">
          <p className="text-[12px] text-ink-gray-700 dark:text-ink-gray-300 truncate">
            {model.company ?? "Other"} <span className="text-ink-gray-400 dark:text-ink-gray-500">/ {model.provider ?? "—"}</span>
          </p>
        </div>

        {/* Multiplier */}
        <div className="text-center">
          <span className="text-[10px] text-ink-gray-400 dark:text-ink-gray-500 mr-0.5">×</span>
          <span className="text-[12px] tabular-nums text-ink-gray-700 dark:text-ink-gray-300">
            {model.usage_multiplier ?? "—"}
          </span>
        </div>

        {/* Usage */}
        <div className="flex items-center justify-end">
          <UsageBadge usageType={model.usage_type} />
        </div>

        {/* Expand chevron */}
        <ChevronDown
          className={`w-4 h-4 text-ink-gray-400 flex-shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </div>

      {/* Description sub-row */}
      {model.description && (
        <div
          onClick={() => setOpen((o) => !o)}
          className="pb-2 pl-20 pr-1 cursor-grab active:cursor-grabbing"
        >
          <p className="text-[12px] text-ink-gray-500 dark:text-ink-gray-400 leading-relaxed">
            {model.description}
          </p>
        </div>
      )}

      {/* Expanded: advanced JSON editor */}
      {open && (
        <div className="pb-4 pt-2 pl-20 pr-1 flex flex-col gap-2">
          <div className="rounded-lg border border-edge-gray-200 dark:border-edge-gray-700 overflow-hidden">
            {/* Editor toolbar */}
            <div className="flex items-center justify-between px-3 py-1.5 bg-surface-muted-deep dark:bg-surface-muted-deep border-b border-edge-gray-200 dark:border-edge-gray-700">
              <div className="flex items-center gap-1.5">
                <Code2 className="w-3.5 h-3.5 text-ink-gray-400" />
                <span className="text-[11px] font-semibold uppercase tracking-wider text-ink-gray-400 dark:text-ink-gray-500">
                  Advanced Config (JSON)
                </span>
                <CustomTooltip content={ADVANCED_FIELDS_HELP} side="right">
                  <Info className="w-3.5 h-3.5 text-ink-gray-400 hover:text-ink-gray-500 cursor-help" />
                </CustomTooltip>
              </div>
              {jsonError ? (
                <span className="text-[11px] font-medium text-ink-red-500 dark:text-ink-red-400">Invalid JSON</span>
              ) : isJsonDirty ? (
                <span className="text-[11px] font-medium text-ink-hex-d97706 dark:text-ink-amber-400">Unsaved changes</span>
              ) : (
                <span className="text-[11px] text-ink-gray-400">Saved</span>
              )}
            </div>
            <textarea
              value={jsonDraft}
              onChange={handleJsonChange}
              rows={14}
              spellCheck={false}
              className={`w-full px-4 py-3 font-mono text-[12.5px] leading-relaxed bg-fill-white dark:bg-fill-hex-0f0f10 text-ink-ink dark:text-ink-gray-200 focus:outline-none resize-y border-0 ${
                jsonError ? "ring-2 ring-inset ring-focus-red-400/60" : ""
              }`}
            />
          </div>
          {jsonError && (
            <p className="text-[11px] text-ink-red-500 dark:text-ink-red-400 font-mono">{jsonError}</p>
          )}
          <div className="flex items-center justify-between pt-3 mt-1 border-t border-edge-gray-100 dark:border-edge-hex-2d2d30">
            <div className="flex items-center gap-3">
              {(isJsonDirty || isPending) && (
                <>
                  <CompactButton
                    onClick={handleSaveJson}
                    disabled={isPending || orderSaving || !!jsonError}
                  >
                    {isPending ? "Saving…" : "Save JSON"}
                  </CompactButton>
                  {!isPending && (
                    <button
                      type="button"
                      onClick={() => { setJsonDraft(savedJson); setJsonError(null); }}
                      className="text-[12px] text-ink-gray-400 hover:text-ink-gray-700 dark:hover:text-ink-gray-300"
                    >
                      Discard
                    </button>
                  )}
                </>
              )}
            </div>
            <button
              type="button"
              onClick={() => setPendingDelete(true)}
              disabled={orderSaving}
              className="flex items-center gap-1.5 text-[12px] text-ink-red-500 hover:text-ink-red-600 dark:text-ink-red-400 dark:hover:text-ink-red-300 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Delete model
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={pendingDelete}
        title={`Delete ${model.model_name ?? model.model_id}?`}
        confirmLabel="Delete"
        confirmVariant="danger"
        isLoading={isDeleting || orderSaving}
        onCancel={() => setPendingDelete(false)}
        onConfirm={() =>
          deleteModel(
            { orgId, userId, modelId: model.model_id },
            {
              onSuccess: () => { setPendingDelete(false); showFeedback(`${model.model_name ?? model.model_id} deleted`, "success"); },
              onError:   () => showFeedback("Failed to delete model", "error"),
            }
          )
        }
      >
        {isDefaultModel && (
          <AlertBanner variant="warning" className="mb-3 text-left">
            This model is currently set as a role default and cannot be deleted until those defaults are reassigned.
          </AlertBanner>
        )}
        This will permanently remove <strong>{model.model_name ?? model.model_id}</strong> from your model catalog. This action cannot be undone.
      </ConfirmDialog>
    </div>
  );
};

// ─── Model Defaults components ───────────────────────────────────────────────
const DefaultsSectionLabel = ({ children }) => (
  <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-gray-400 dark:text-ink-gray-500 pb-1 border-b border-edge-gray-200 dark:border-edge-gray-700">
    {children}
  </p>
);

const TEXT_ROLES = [
  { key: "default_text_model",   uppercaseKey: "DEFAULT_TEXT_MODEL",   label: "Text Generation",  hint: "General chat and generation",          icon: MessageSquare },
  { key: "default_memory_model", uppercaseKey: "DEFAULT_MEMORY_MODEL", label: "Memory",           hint: "Long-term memory summarisation",         icon: Brain         },
  { key: "default_title_model",  uppercaseKey: "DEFAULT_TITLE_MODEL",  label: "Title Generation", hint: "Chat title auto-generation",            icon: PenLine       },
  { key: "default_intent_model", uppercaseKey: "DEFAULT_INTENT_MODEL", label: "Intent Detection", hint: "Routing and intent classification",     icon: Crosshair     },
  { key: "default_inteference_model", uppercaseKey: "DEFAULT_INTEFERENCE_MODEL", label: "Policy Classification", hint: "Agent tool policy classification", icon: Crosshair },
  { key: "default_toc_model",    uppercaseKey: "DEFAULT_TOC_MODEL",    label: "TOC / Summary",    hint: "Document table-of-contents generation", icon: List          },
];

const MEDIA_ROLES = [
  { key: "default_image_model", uppercaseKey: "DEFAULT_IMAGE_MODEL", label: "Image Generation", hint: "Image creation and editing",  icon: Image, mediaType: "image" },
  { key: "default_video_model", uppercaseKey: "DEFAULT_VIDEO_MODEL", label: "Video Generation", hint: "Video generation",            icon: Video, mediaType: "video" },
  { key: "default_audio_model", uppercaseKey: "DEFAULT_AUDIO_MODEL", label: "Audio Generation", hint: "Music and audio generation",  icon: Music, mediaType: "audio" },
];

const ALL_ROLES = [...TEXT_ROLES, ...MEDIA_ROLES];

const getDefaultOptions = (models, mediaType, currentValue) => {
  const filtered = (models ?? []).filter((m) => m.media_type === mediaType);
  const asOptions = filtered.map((m) => ({
    value: m.model_id,
    label: `${m.model_name}${m.usage_type === "user" ? "" : ` (${m.usage_type})`}`,
  }));
  const inList = asOptions.some((o) => o.value === currentValue);
  if (currentValue && !inList) asOptions.push({ value: currentValue, label: `${currentValue} (missing)` });
  return asOptions;
};

const DefaultField = ({ role, value, models, onChange, isLast }) => {
  const mediaType = role.mediaType ?? "text";
  const options = getDefaultOptions(models, mediaType, value);
  const RoleIcon = role.icon;
  return (
    <div className={`flex items-center gap-4 py-3.5 ${
      isLast ? "" : "border-b border-edge-gray-100 dark:border-edge-hex-2d2d30"
    }`}>
      <div className="photon-settings-icon flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center bg-fill-indigo-50 dark:bg-fill-hex-1e293b">
        <RoleIcon className="w-[17px] h-[17px] text-ink-primary dark:text-ink-accent" strokeWidth={1.8} />
      </div>
      <div className="w-44 flex-shrink-0">
        <p className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">{role.label}</p>
        <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500 mt-0.5">{role.hint}</p>
      </div>
      <div className="w-80 flex-shrink-0">
        <Select
          value={value ?? ""}
          onChange={(v) => onChange(role.key, v)}
          options={options}
          placeholder="Select a model"
        />
      </div>
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────
const ModelConfigPage = () => {
  const orgId  = UserAuthStore((state) => state.orgId);
  const userId = UserAuthStore((state) => state.user?.uid);
  const { showFeedback } = useFeedback();

  const { data: models, isLoading } = useGetAllModels();
  const { mutate: updateOrder, isPending: isSavingOrder } = useUpdateModelOrder();
  const modelWrites = useIsMutating({
    predicate: (mutation) => [QUERYKEYS.UPDATE_MODEL, QUERYKEYS.CREATE_MODEL, QUERYKEYS.DELETE_MODEL]
      .includes(mutation.options.mutationKey?.[0]),
  });
  const orderDisabled = isSavingOrder || modelWrites > 0;
  const [pendingOrder, setPendingOrder] = useState(null);
  const [draggedId, setDraggedId] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const orderLock = useRef(false);
  const orderedModels = pendingOrder?.orgId === orgId ? pendingOrder.models : sortModels(models);

  const clearDrag = () => { setDraggedId(null); setDropTargetId(null); };

  const moveModel = (sourceId, targetId) => {
    clearDrag();
    if (orderLock.current || orderDisabled || sourceId === targetId) return;
    const from = orderedModels.findIndex((model) => model.model_id === sourceId);
    const to = orderedModels.findIndex((model) => model.model_id === targetId);
    if (from < 0 || to < 0) return;
    if (usageRank(orderedModels[from]) !== usageRank(orderedModels[to])) return;
    const reordered = [...orderedModels];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    const numbered = reordered.map((model, index) => ({ ...model, sort_order: index + 1 }));
    const changed = numbered.filter((model) =>
      orderedModels.find((original) => original.model_id === model.model_id).sort_order !== model.sort_order
    );
    orderLock.current = true;
    setPendingOrder({ orgId, models: numbered });
    updateOrder({ orgId, userId, models: changed }, {
      onError: () => {
        showFeedback("Failed to save model order. Some changes may have saved; reload the page to verify before retrying.", "error");
      },
      onSettled: () => { setPendingOrder(null); orderLock.current = false; },
    });
  };

  // ── Model defaults ───────────────────────────────────────────────────────────
  const { data: defaults, isLoading: defaultsLoading } = useGetModelDefaults(orgId);
  const { mutate: updateDefaults, isPending: isSavingDefaults } = useUpdateModelDefaults();
  const [drafts, setDrafts] = useState({});
  const [addModelOpen, setAddModelOpen] = useState(false);
  const [newModelJson, setNewModelJson] = useState(ADD_MODEL_TEMPLATE);
  const [newModelJsonError, setNewModelJsonError] = useState(null);
  const { mutate: createModel, isPending: isCreating } = useCreateModel();

  const closeAddModal = () => { setAddModelOpen(false); setNewModelJson(ADD_MODEL_TEMPLATE); setNewModelJsonError(null); };

  const handleCreateModel = () => {
    let parsed;
    try { parsed = JSON.parse(newModelJson); } catch { return; }
    const { model_id, ...rest } = parsed;
    const model_data = Object.fromEntries(
      Object.entries(rest).map(([k, v]) => [k.toUpperCase(), v])
    );
    createModel(
      { orgId, userId, modelId: model_id, modelData: model_data },
      {
        onSuccess: () => { closeAddModal(); showFeedback(`${parsed.model_name ?? model_id} added`, "success"); },
        onError:   () => showFeedback("Failed to add model", "error"),
      }
    );
  };

  useEffect(() => {
    if (defaults) {
      const initial = {};
      ALL_ROLES.forEach(({ key }) => { initial[key] = defaults[key] ?? ""; });
      setDrafts(initial);
    }
  }, [defaults]);

  const isDefaultsDirty = defaults
    ? ALL_ROLES.some(({ key }) => drafts[key] !== (defaults[key] ?? ""))
    : false;

  const handleDefaultChange = (key, value) => setDrafts((prev) => ({ ...prev, [key]: value }));

  const handleSaveDefaults = () => {
    const payload = {};
    ALL_ROLES.forEach(({ key, uppercaseKey }) => { if (drafts[key]) payload[uppercaseKey] = drafts[key]; });
    updateDefaults(
      { orgId, userId, defaults: payload },
      {
        onSuccess: () => showFeedback("Model defaults updated", "success"),
        onError:   () => showFeedback("Failed to update defaults", "error"),
      }
    );
  };

  return (
    <SettingsPageShell
      title="Model Config"
      description="Adjust generation parameters for each model across your organisation."
      maxWidth="4xl"
    >
      {isLoading ? (
        <Loader variant="spinner" />
      ) : (
        <div className="flex flex-col gap-6">
          <div className="flex items-center justify-between gap-4">
            <p id="model-order-help" className="text-[12px] text-ink-gray-500 dark:text-ink-gray-400">
              Drag the handle to reorder models across companies. Or focus it and use Up/Down arrows. Changes save automatically.
            </p>
            <CompactButton onClick={() => setAddModelOpen(true)} disabled={isSavingOrder}>
              <Plus className="w-3 h-3" />
              Add Model
            </CompactButton>
          </div>

          {/* Inline Add Model panel */}
          {addModelOpen && (
            <div className="rounded-xl border border-edge-gray-200 dark:border-edge-gray-700 overflow-hidden">
              {/* Panel header */}
              <div className="flex items-center justify-between px-4 py-3 bg-surface-muted-deep dark:bg-surface-muted-deep border-b border-edge-gray-200 dark:border-edge-gray-700">
                <div className="flex items-center gap-1.5">
                  <Plus className="w-3.5 h-3.5 text-ink-gray-400" />
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-ink-gray-400 dark:text-ink-gray-500">New Model</span>
                </div>
                <span className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">
                  Add model-specific parameters (temperature, top_k…) below the required fields.
                </span>
              </div>
              {/* JSON editor */}
              <textarea
                value={newModelJson}
                onChange={(e) => {
                  setNewModelJson(e.target.value);
                  try { JSON.parse(e.target.value); setNewModelJsonError(null); }
                  catch (err) { setNewModelJsonError(`Invalid JSON: ${err.message}`); }
                }}
                rows={14}
                spellCheck={false}
                className={`w-full px-4 py-3 font-mono text-[12.5px] leading-relaxed bg-fill-white dark:bg-fill-hex-0f0f10 text-ink-ink dark:text-ink-gray-200 focus:outline-none resize-y border-0 ${
                  newModelJsonError ? "ring-2 ring-inset ring-focus-red-400/60" : ""
                }`}
              />
              {/* Actions */}
              <div className="flex items-center justify-between px-4 py-3 bg-surface-muted-deep dark:bg-surface-muted-deep border-t border-edge-gray-200 dark:border-edge-gray-700">
                {newModelJsonError
                  ? <p className="text-[11px] text-ink-red-500 dark:text-ink-red-400 font-mono">{newModelJsonError}</p>
                  : <span />}
                <div className="flex items-center gap-3">
                  <ThirdButton onClick={closeAddModal}>Cancel</ThirdButton>
                  <PrimaryButton onClick={handleCreateModel} disabled={!!newModelJsonError || isCreating || isSavingOrder}>
                    {isCreating ? "Adding…" : "Add Model"}
                  </PrimaryButton>
                </div>
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <div className="min-w-[720px]">
              <ColumnHeaders />
              <div aria-label="Model order" aria-busy={isSavingOrder}>
                {orderedModels.map((model, i) => (
                  <ModelRow
                    key={model.model_id}
                    model={model}
                    orgId={orgId}
                    userId={userId}
                    defaults={defaults}
                    isLast={i === orderedModels.length - 1}
                    orderSaving={orderDisabled}
                    orderControls={{
                      isDragging: draggedId === model.model_id,
                      isTarget: dropTargetId === model.model_id && draggedId !== model.model_id,
                      onDragStart: (event, rowElement) => {
                        event.dataTransfer.effectAllowed = "move";
                        event.dataTransfer.setData("text/plain", model.model_id);
                        if (rowElement && event.dataTransfer.setDragImage) {
                          const rect = rowElement.getBoundingClientRect();
                          const offsetX = Number.isFinite(event.clientX) && rect.width
                            ? Math.max(0, Math.min(event.clientX - rect.left, rect.width))
                            : 12;
                          const offsetY = Number.isFinite(event.clientY) && rect.height
                            ? Math.max(0, Math.min(event.clientY - rect.top, rect.height))
                            : 20;
                          event.dataTransfer.setDragImage(rowElement, offsetX, offsetY);
                        }
                        setDraggedId(model.model_id);
                      },
                      onDragEnd: clearDrag,
                      onDragOver: (event) => {
                        if (!draggedId || orderDisabled) return;
                        const draggedModel = orderedModels.find((m) => m.model_id === draggedId);
                        if (!draggedModel || usageRank(draggedModel) !== usageRank(model)) return;
                        event.preventDefault();
                        event.dataTransfer.dropEffect = "move";
                        setDropTargetId(model.model_id);
                      },
                      onDrop: (event) => {
                        event.preventDefault();
                        if (draggedId) moveModel(draggedId, model.model_id);
                      },
                      onKeyDown: (event) => {
                        if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
                        event.preventDefault();
                        const target = orderedModels[i + (event.key === "ArrowUp" ? -1 : 1)];
                        if (target) moveModel(model.model_id, target.model_id);
                      },
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
          {/* ── Model Defaults ───────────────────────────────────────────── */}
          <div className="pt-6 mt-2 border-t border-edge-gray-200 dark:border-edge-gray-700">
            <div className="mb-5">
              <h2 className="text-[15px] font-semibold text-ink-ink dark:text-ink-white">Model Defaults</h2>
              <p className="text-[12px] text-ink-gray-400 dark:text-ink-gray-500 mt-0.5">
                Configure which model handles each platform role across your organisation.
              </p>
            </div>
            {defaultsLoading ? (
              <Loader variant="spinner" />
            ) : (
              <div className="flex flex-col gap-6">
                <div className="flex flex-col">
                  <DefaultsSectionLabel>Text models</DefaultsSectionLabel>
                  <div className="mt-1">
                    {TEXT_ROLES.map((role, i) => (
                      <DefaultField
                        key={role.key}
                        role={role}
                        value={drafts[role.key]}
                        models={models}
                        onChange={handleDefaultChange}
                        isLast={i === TEXT_ROLES.length - 1}
                      />
                    ))}
                  </div>
                </div>
                <div className="flex flex-col">
                  <DefaultsSectionLabel>Media models</DefaultsSectionLabel>
                  <div className="mt-1">
                    {MEDIA_ROLES.map((role, i) => (
                      <DefaultField
                        key={role.key}
                        role={role}
                        value={drafts[role.key]}
                        models={models}
                        onChange={handleDefaultChange}
                        isLast={i === MEDIA_ROLES.length - 1}
                      />
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-3 pt-2">
                  <PrimaryButton onClick={handleSaveDefaults} disabled={!isDefaultsDirty || isSavingDefaults}>
                    {isSavingDefaults ? "Saving…" : "Save Defaults"}
                  </PrimaryButton>
                </div>
              </div>
            )}
          </div>        </div>
      )}
    </SettingsPageShell>
  );
};

export default ModelConfigPage;
